// ============================================================================
// @gs/feedback/client — mentions dans le compositeur (helpers PURS, testes sans DOM)
// ============================================================================
// feat-feedback-les-mentions-de-personnes (prototype ZoneSecur components/feedback/
// MentionTextarea.tsx, F-FBK-19, -25, -27, -28). Trois gestes :
//   - lire le « @debut » tape juste avant le curseur (la liste s ouvre) ;
//   - remplacer ce « @debut » par « @Nom complet » suivi d une espace, et rendre la position du
//     curseur (le focus revient a la zone de saisie, F-FBK-27) ;
//   - a l envoi, ne garder que les personnes dont « @Nom » est encore dans le texte (une mention
//     effacee a la main ne part pas).

import { MENTIONS_MAX } from '../schemas';

/** Le « @debut » en cours de saisie : sa position dans le texte et ce qui suit le « @ ». */
export interface MentionEnCours {
  /** Index du « @ » dans le texte. */
  debut: number;
  /** Ce qui suit le « @ », jusqu au curseur (peut etre vide). */
  saisie: string;
}

/** Au plus ce nombre de caracteres apres « @ » : au-dela, ce n est plus une recherche de nom. */
const SAISIE_MAX = 40;

/**
 * Le « @debut » qui se termine au curseur, ou null. Le « @ » ouvre une mention en debut de texte
 * ou apres une espace (jamais au milieu d un courriel : « d@exemple ») ; la saisie s arrete a la
 * fin de ligne.
 */
export function mentionEnCours(texte: string, curseur: number): MentionEnCours | null {
  const avant = texte.slice(0, Math.max(0, Math.min(curseur, texte.length)));
  const at = avant.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(avant[at - 1])) return null;
  const saisie = avant.slice(at + 1);
  if (saisie.length > SAISIE_MAX || /[\n\r@]/.test(saisie)) return null;
  // Deux espaces de suite ou une espace en tete : la mention est abandonnee.
  if (/^\s|\s\s/.test(saisie)) return null;
  return { debut: at, saisie };
}

/** Remplace le « @debut » par « @Nom » et une espace ; rend le texte et le nouveau curseur. */
export function insererMention(
  texte: string,
  enCours: MentionEnCours,
  curseur: number,
  nom: string,
): { texte: string; curseur: number } {
  const insere = `@${nom} `;
  const fin = Math.max(enCours.debut + 1, Math.min(curseur, texte.length));
  const apres = texte.slice(fin).replace(/^ /, '');
  const nouveau = texte.slice(0, enCours.debut) + insere + apres;
  return { texte: nouveau, curseur: enCours.debut + insere.length };
}

/**
 * Les identifiants a envoyer : les personnes choisies dont « @Nom » figure encore dans le texte,
 * sans doublon, au plus MENTIONS_MAX. Le serveur refiltre de toute facon au cercle du ticket.
 */
export function mentionsAEnvoyer(texte: string, choisies: ReadonlyMap<string, string>): string[] {
  const out: string[] = [];
  for (const [id, nom] of choisies) {
    if (out.length >= MENTIONS_MAX) break;
    if (texte.includes(`@${nom}`) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Element suivant ou precedent de la liste, en boucle (fleches haut et bas, F-FBK-28). */
export function surbrillanceSuivante(index: number, total: number, pas: 1 | -1): number {
  if (total <= 0) return 0;
  return (index + pas + total) % total;
}
